import { addDays, formatShortDate, isoWeekdayOf } from './format';

export const PERIOD_KEYS = [
  'today',
  'yesterday',
  'this_week',
  'last_week',
  'this_month',
  'last_month',
  '7',
  '30',
  '90',
  'custom',
] as const;
export type PeriodKey = (typeof PERIOD_KEYS)[number];

export const PERIOD_LABEL: Record<PeriodKey, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  this_week: 'This week',
  last_week: 'Last week',
  this_month: 'This month',
  last_month: 'Last month',
  '7': 'Last 7 days',
  '30': 'Last 30 days',
  '90': 'Last 90 days',
  custom: 'Custom range',
};

/** The most days the API answers for at a time. */
export const MAX_PERIOD_DAYS = 92;

export interface PeriodValue {
  period: PeriodKey;
  /** Only for `custom`. */
  from?: string;
  to?: string;
}

export interface ResolvedPeriod {
  from: string;
  to: string;
  days: number;
  label: string;
}

const DATE = /^\d{4}-\d{2}-\d{2}$/;

export const daysInRange = (from: string, to: string): number =>
  Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000) + 1;

const startOfWeek = (date: string, weekStart: 'monday' | 'sunday'): string => {
  const first = weekStart === 'monday' ? 1 : 7;
  return addDays(date, -((isoWeekdayOf(date) - first + 7) % 7));
};

const bounds = (
  period: Exclude<PeriodKey, 'custom'>,
  today: string,
  weekStart: 'monday' | 'sunday',
): { from: string; to: string } => {
  switch (period) {
    case 'today':
      return { from: today, to: today };
    case 'yesterday': {
      const y = addDays(today, -1);
      return { from: y, to: y };
    }
    case 'this_week':
      return { from: startOfWeek(today, weekStart), to: today };
    case 'last_week': {
      const to = addDays(startOfWeek(today, weekStart), -1);
      return { from: addDays(to, -6), to };
    }
    case 'this_month':
      return { from: `${today.slice(0, 7)}-01`, to: today };
    case 'last_month': {
      const to = addDays(`${today.slice(0, 7)}-01`, -1);
      return { from: `${to.slice(0, 7)}-01`, to };
    }
    default:
      return { from: addDays(today, -(Number(period) - 1)), to: today };
  }
};

/** A broken custom range (from a pasted address, say) reads as the last 30 days. */
const validCustom = (from: string | undefined, to: string | undefined, today: string) =>
  Boolean(
    from &&
    to &&
    DATE.test(from) &&
    DATE.test(to) &&
    from <= to &&
    to <= today &&
    daysInRange(from, to) <= MAX_PERIOD_DAYS,
  );

/** The two dates a choice means today, in the company's calendar. */
export function resolvePeriod(
  value: PeriodValue,
  today: string,
  weekStart: 'monday' | 'sunday' = 'monday',
): ResolvedPeriod {
  if (value.period === 'custom' && validCustom(value.from, value.to, today)) {
    const { from, to } = value as { from: string; to: string };
    return {
      from,
      to,
      days: daysInRange(from, to),
      label:
        from === to ? formatShortDate(from) : `${formatShortDate(from)} – ${formatShortDate(to)}`,
    };
  }
  const key = value.period === 'custom' ? '30' : value.period;
  const { from, to } = bounds(key, today, weekStart);
  return { from, to, days: daysInRange(from, to), label: PERIOD_LABEL[key] };
}

/** For a route's validateSearch: an unknown period falls back to the given one. */
export function parsePeriodSearch(s: Record<string, unknown>, fallback: PeriodKey): PeriodValue {
  const raw = String(s['period']);
  const period = (PERIOD_KEYS as readonly string[]).includes(raw) ? (raw as PeriodKey) : fallback;
  if (period !== 'custom') return { period };
  return {
    period,
    from: typeof s['from'] === 'string' ? s['from'] : undefined,
    to: typeof s['to'] === 'string' ? s['to'] : undefined,
  };
}
